import * as THREE from "three";

const LIFE = 0.9;
/** Fresh out of the nozzle a puff is hot: warm and brighter than white (so it blooms), cooling to grey by this far through its life. */
const HOT = 0.2;

/** A soft round puff, white in the middle fading out to nothing. */
const puffTexture = (() => {
  const c = document.createElement("canvas");
  c.width = c.height = 32;
  const ctx = c.getContext("2d")!;
  const g = ctx.createRadialGradient(16, 16, 0, 16, 16, 16);
  g.addColorStop(0, "rgba(255,255,255,1)");
  g.addColorStop(0.5, "rgba(255,255,255,0.5)");
  g.addColorStop(1, "rgba(255,255,255,0)");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 32, 32);
  return new THREE.CanvasTexture(c);
})();

interface Puff {
  x: number;
  y: number;
  vx: number;
  vy: number;
  age: number;
  spin: number;
}

/**
 * Steam blowing out of firing engines: puffs that drift out of the nozzle,
 * swell, slow and fade. One instanced mesh for all of them. Additive, so
 * fading a puff's colour to black fades it out.
 */
export class SteamPuffs {
  readonly mesh: THREE.InstancedMesh;
  private readonly puffs: Puff[] = [];
  private readonly m = new THREE.Matrix4();
  private readonly q = new THREE.Quaternion();
  private readonly s = new THREE.Vector3();
  private readonly p = new THREE.Vector3();
  private readonly c = new THREE.Color();
  private readonly z = new THREE.Vector3(0, 0, 1);

  /** `tint`: puffs of this colour, fading out (spores), not steam cooling from hot to grey. */
  constructor(
    private readonly capacity = 800,
    private readonly tint?: readonly [number, number, number],
  ) {
    this.mesh = new THREE.InstancedMesh(
      new THREE.PlaneGeometry(1, 1),
      new THREE.MeshBasicMaterial({ map: puffTexture, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending }),
      capacity,
    );
    this.mesh.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(capacity * 3), 3);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = 6;
    this.mesh.count = 0;
  }

  /** Blow a puff from (x, y) along (dx, dy) (a unit vector), on top of the ship's own velocity. */
  emit(x: number, y: number, dx: number, dy: number, shipVx = 0, shipVy = 0): void {
    if (this.puffs.length >= this.capacity) this.puffs.shift();
    const speed = 10 + Math.random() * 6;
    const wobble = (Math.random() - 0.5) * 3.2;
    this.puffs.push({
      x: x + (Math.random() - 0.5) * 0.6,
      y: y + (Math.random() - 0.5) * 0.6,
      vx: dx * speed - dy * wobble + shipVx,
      vy: dy * speed + dx * wobble + shipVy,
      age: 0,
      spin: Math.random() * Math.PI * 2,
    });
  }

  update(dt: number): void {
    let n = 0;
    for (let i = 0; i < this.puffs.length; i++) {
      const p = this.puffs[i]!;
      p.age += dt;
      if (p.age >= LIFE) continue;
      // Air slows them fast; what's left of the puff lingers and spreads.
      const slow = Math.exp(-dt * 3.5);
      p.vx *= slow;
      p.vy *= slow;
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      this.puffs[n++] = p;
      const t = p.age / LIFE;
      const size = 1 + t * 4.4;
      this.m.compose(this.p.set(p.x, p.y, 0.4), this.q.setFromAxisAngle(this.z, p.spin), this.s.set(size, size, 1));
      this.mesh.setMatrixAt(n - 1, this.m);
      if (this.tint) {
        const f = (1 - t) * (1 - t);
        this.mesh.setColorAt(n - 1, this.c.setRGB(this.tint[0] * f, this.tint[1] * f, this.tint[2] * f));
        continue;
      }
      const grey = 0.55 * (1 - t) * (1 - t);
      const heat = Math.max(0, 1 - t / HOT);
      const hot = heat * heat;
      this.mesh.setColorAt(n - 1, this.c.setRGB(grey + 1.5 * hot, grey + 0.8 * hot, grey + 0.3 * hot));
    }
    this.puffs.length = n;
    this.mesh.count = n;
    this.mesh.instanceMatrix.needsUpdate = true;
    if (this.mesh.instanceColor) this.mesh.instanceColor.needsUpdate = true;
  }
}
